import { Alert } from '@designsforhealth/dfh-react-components'
import React, { useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'

import { GetCountryDocument } from '../../../../../graphql/api'
import useApiLazyQuery from '../../../../../hooks/useApiLazyQuery'
import useLogAndCaptureError from '../../../../../hooks/useLogAndCaptureError'
import { SelectFieldOption } from '../../../../../lib/form'
import {
  addressMaxLengthProp,
  addressMaxLengthRule,
  determineFieldLabels,
} from '../../../../../lib/form/address'
import ErrorRetrievingDataMessage from '../../../../ErrorRetrievingDataMessage'
import SelectField from '../../../../form/SelectField'
import TextField from '../../../../form/TextField'
import type { DispensaryContactFormValues } from './PractitionerDispensaryContact'

export interface DispensaryAddressFormProps {
  countriesList: SelectFieldOption[]
  lang?: string | null
}

export const DispensaryAddressForm: React.FC<DispensaryAddressFormProps> = ({
  countriesList,
  lang,
}) => {
  const [fetchCountryData, { error: countryError, data: countryData, loading: countryLoading }] =
    useApiLazyQuery(GetCountryDocument)
  useLogAndCaptureError(countryError)

  const { watch } = useFormContext<DispensaryContactFormValues>()
  const currentCountryCode = watch('countryCode')

  const fieldLabels = useMemo(() => determineFieldLabels(lang), [lang])

  useEffect(() => {
    if (!currentCountryCode) {
      return
    }
    fetchCountryData({
      variables: {
        countryCode: currentCountryCode,
      },
    })
  }, [currentCountryCode, fetchCountryData])

  const regionsList = useMemo(
    () =>
      countryData?.country?.regions?.map(({ iso: value, name: label }) => ({
        label,
        value,
      })) ?? [],
    [countryData],
  )

  return (
    <>
      {countryError && (
        <Alert type="error">
          <ErrorRetrievingDataMessage />
        </Alert>
      )}
      <TextField
        autoComplete="address-line1"
        label="Address"
        name="street1"
        inputProps={addressMaxLengthProp}
        rules={addressMaxLengthRule}
      />
      <TextField
        autoComplete="address-line2"
        label="Apartment, unit, suite, etc. (optional)"
        name="street2"
        inputProps={addressMaxLengthProp}
        rules={addressMaxLengthRule}
      />
      <TextField autoComplete="address-level2" label="City" name="city" />
      <SelectField
        className="dropdown-basic"
        autoComplete="address-level1"
        label={countryLoading ? `${fieldLabels.state} (loading...)` : fieldLabels.state}
        name="region"
        placeholder="Select"
        options={regionsList}
        disabled={countryLoading}
      />
      <TextField autoComplete="postal-code" label={fieldLabels.zipCode} name="postcode" />
      <SelectField
        className="dropdown-basic"
        autoComplete="country"
        label={'Country'}
        name="countryCode"
        placeholder="Select"
        options={countriesList}
      />
    </>
  )
}
